/**
 * @author    	Nikko Renolds
 * @package		  Trap Tycoon
 * @link		    https://github.com/Ni1kko/Trap-Tycoon
 * @copyright 	(c) Fallox Developments2026.
 * @license		  MIT
 * @description Frontend component representing the Street Arena where players can find fights.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Users,
  Shield,
  Landmark,
  TrendingUp,
  Crown,
  Zap,
  Target,
  ArrowUpCircle,
  XCircle,
  Search,
  PlusCircle,
  UserPlus,
  Info,
  DollarSign,
  Clock,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";

export default function AllianceHQ({ stats, onUpdate }) {
  const { addNotification } = useNotify();
  const [alliance, setAlliance] = useState(null);
  const [activeTab, setActiveTab] = useState("OVERVIEW");
  const [donationAmount, setDonationAmount] = useState(0);
  const [viewMode, setViewMode] = useState("LANDING"); // LANDING, CREATE, SEARCH
  const [allianceList, setAllianceList] = useState([]);
  const [createData, setCreateData] = useState({ name: "", tag: "" });
  const [feed, setFeed] = useState([]);
  const [activeMission, setActiveMission] = useState(null);
  const [availableMissions, setAvailableMissions] = useState([]);
  const [updateName, setUpdateName] = useState("");
  const [updateTag, setUpdateTag] = useState("");
  const [requests, setRequests] = useState([]);
  const [playerSearch, setPlayerSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const fetchRequests = useCallback(async () => {
    try {
      const data = await callBackend("/alliance/requests");
      setRequests(data);
    } catch (err) {
      addNotification("Failed to load applicants.", "error");
    }
  }, [addNotification]);

  const fetchHQ = async () => {
    try {
      const data = await callBackend("/alliance/hq");
      setAlliance(data);
    } catch (err) {
      setAlliance(false); // Player is currently unaligned
    }
  };

  const fetchAlliances = async () => {
    try {
      const data = await callBackend("/alliance/list");
      setAllianceList(data);
      setViewMode("SEARCH");
    } catch (err) {
      addNotification("Intelligence gathering failed.", "error");
    }
  };

  const handleCreateAlliance = async (e) => {
    e.preventDefault();
    try {
      const data = await callBackend("/alliance/create", {
        method: "POST",
        body: {
          ...createData,
          clientStats: stats,
        },
      });
      addNotification(data.message, "success");
      fetchHQ(); // Switch to the HQ view
      onUpdate(); // Update user cash stats
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleJoinAlliance = async (id) => {
    try {
      const data = await callBackend(`/alliance/join/${id}`, {
        method: "POST",
      });
      addNotification(data.message, "success");
      fetchAlliances(); // Refresh list to show pending state
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleApprove = async (requestId) => {
    try {
      const data = await callBackend("/alliance/request/approve", {
        method: "POST",
        body: { requestId },
      });
      addNotification(data.message, "success");
      fetchRequests(); // Refresh the recruitment list
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleDeny = async (requestId) => {
    try {
      const data = await callBackend("/alliance/request/deny", {
        method: "POST",
        body: { requestId },
      });
      addNotification(data.message, "success");
      fetchRequests();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleDonate = async () => {
    try {
      const data = await callBackend("/alliance/donate", {
        method: "POST",
        body: { amount: parseInt(donationAmount), clientStats: stats },
      });
      addNotification(data.message, "success");
      setAlliance(data.updatedAlliance);
      setDonationAmount(0);
      onUpdate();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleUpgradeSlots = async () => {
    try {
      const data = await callBackend("/alliance/upgrade-slots", {
        method: "POST",
      });
      addNotification(data.message, "success");
      setAlliance(data.updatedAlliance);
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleLeave = async () => {
    const promptMsg =
      stats.id === alliance.leader_id
        ? "LEADER DISBAND PROTOCOL: Disbanding will delete the crew and treasury. Proceed?"
        : "Exit the tactical network and return to solo operations?";

    if (!window.confirm(promptMsg)) return;

    try {
      const data = await callBackend("/alliance/leave", { method: "POST" });
      addNotification(data.message, "success");
      onUpdate();
      setAlliance(false);
      setViewMode("LANDING");
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleKick = async (targetId) => {
    if (!window.confirm("PURGE THIS MEMBER FROM THE RANKS?")) return;
    try {
      const data = await callBackend("/alliance/kick", {
        method: "POST",
        body: { targetId },
      });
      addNotification(data.message, "success");
      const updated = await callBackend("/alliance/hq");
      setAlliance(updated);
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleStartMission = async (missionId) => {
    try {
      const data = await callBackend("/alliance/missions/start", {
        method: "POST",
        body: { missionId },
      });
      addNotification(data.message, "success");
      setActiveTab("MISSIONS");
      const active = await callBackend("/alliance/missions/active");
      setActiveMission(active);
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleClaimMission = async () => {
    try {
      const data = await callBackend("/alliance/missions/claim", {
        method: "POST",
      });
      addNotification(data.message, "success");
      setActiveMission(null);
      onUpdate();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleUpdateName = async () => {
    try {
      const data = await callBackend("/alliance/update-name", {
        method: "POST",
        body: { newName: updateName },
      });
      addNotification(data.message, "success");
      fetchHQ();
      onUpdate();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleUpdateTag = async () => {
    try {
      const data = await callBackend("/alliance/update-tag", {
        method: "POST",
        body: { newTag: updateTag },
      });
      addNotification(data.message, "success");
      fetchHQ();
      onUpdate();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleSearchPlayers = async (val) => {
    setPlayerSearch(val);
    if (val.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const results = await callBackend(
        `/alliance/search-players?query=${val}`
      );
      setSearchResults(results);
    } catch (err) {
      addNotification("Search disrupted.", "error");
    }
  };

  const handleInvitePlayer = async (targetId) => {
    try {
      const data = await callBackend("/alliance/invite", {
        method: "POST",
        body: { targetId },
      });
      addNotification(data.message, "success");
      setSearchResults((prev) => prev.filter((p) => p.id !== targetId));
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  // Helper for Rarity Cards
  const getRarityTheme = (level) => {
    if (level >= 50)
      return {
        border: "border-yellow-600",
        bg: "bg-yellow-900/10",
        label: "LEGENDARY",
        textColor: "text-yellow-500",
      };
    if (level >= 25)
      return {
        border: "border-purple-600",
        bg: "bg-purple-900/10",
        label: "EPIC",
        textColor: "text-purple-500",
      };
    if (level >= 10)
      return {
        border: "border-blue-600",
        bg: "bg-blue-900/10",
        label: "RARE",
        textColor: "text-blue-500",
      };
    return {
      border: "border-zinc-700",
      bg: "bg-zinc-900",
      label: "COMMON",
      textColor: "text-zinc-400",
    };
  };

  useEffect(() => {
    fetchHQ();
    if (activeTab === "FEED") {
      callBackend("/alliance/feed").then(setFeed);
    } else if (activeTab === "MISSIONS") {
      callBackend("/alliance/missions/active").then((data) => {
        if (data.status === "active") {
          setActiveMission(data);
        } else {
          setActiveMission(null);
          callBackend("/alliance/missions/list").then(setAvailableMissions);
        }
      });
    } else if (activeTab === "RECRUITMENT") {
      fetchRequests();
    }
  }, [activeTab, fetchRequests]);

  if (alliance === null)
    return (
      <div className="text-zinc-500 font-mono italic p-12 text-center uppercase tracking-tighter">
        Accessing Secure Comms...
      </div>
    );

  // --- RENDERING: NO ALLIANCE (SEARCH/CREATE) ---
  if (alliance === false) {
    return (
      <div className="bg-zinc-950 border-2 border-zinc-800 p-8 font-mono shadow-2xl">
        {viewMode === "LANDING" && (
          <div className="text-center animate-in fade-in duration-300">
            <Users size={64} className="mx-auto text-zinc-800 mb-6" />
            <h2 className="text-3xl font-black text-white uppercase italic mb-2">
              No Alliance Detected
            </h2>
            <p className="text-zinc-500 text-xs mb-8 uppercase tracking-widest">
              Establish your own tactical unit or join an existing crew.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setViewMode("CREATE")}
                className="bg-green-600 hover:bg-green-400 text-black font-black px-8 py-4 uppercase italic text-sm flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(34,197,94,0.1)]"
              >
                <PlusCircle size={18} /> Establish Crew
              </button>
              <button
                onClick={fetchAlliances}
                className="bg-zinc-800 hover:bg-white hover:text-black text-white font-black px-8 py-4 uppercase italic text-sm flex items-center gap-2 transition-all"
              >
                <Search size={18} /> Scan the Block
              </button>
            </div>
          </div>
        )}

        {viewMode === "CREATE" && (
          <div className="max-w-md mx-auto animate-in zoom-in-95 duration-300">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-2xl font-black text-white italic uppercase">
                  New Crew Registration
                </h3>
                <p className="text-[10px] text-zinc-500 uppercase">
                  Initialize tactical network protocols
                </p>
              </div>
              <button
                onClick={() => setViewMode("LANDING")}
                className="text-zinc-600 hover:text-red-500 transition-colors"
              >
                <XCircle size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateAlliance} className="space-y-6">
              <div className="bg-blue-900/10 border border-blue-900/30 p-4">
                <p className="text-[10px] text-blue-400 uppercase leading-tight font-bold flex items-start gap-2">
                  <Info size={16} className="shrink-0" />
                  Protocol requirements: Level 10+ and $100,000 creation fee.
                  Names and Tags are permanent.
                </p>
              </div>

              <div>
                <label className="text-[10px] text-zinc-600 uppercase font-black mb-2 block">
                  Crew Name (Max 50 chars)
                </label>
                <input
                  type="text"
                  maxLength={50}
                  required
                  className="w-full bg-black border border-zinc-800 p-4 text-white focus:border-green-600 outline-none uppercase font-bold"
                  value={createData.name}
                  onChange={(e) =>
                    setCreateData({ ...createData, name: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="text-[10px] text-zinc-600 uppercase font-black mb-2 block">
                  Clan Tag (4 Chars)
                </label>
                <input
                  type="text"
                  maxLength={4}
                  required
                  placeholder="TRAP"
                  className="w-full bg-black border border-zinc-800 p-4 text-white focus:border-green-600 outline-none uppercase font-black text-center tracking-[0.5em] text-2xl"
                  value={createData.tag}
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      tag: e.target.value.toUpperCase(),
                    })
                  }
                />
              </div>

              <button className="w-full bg-green-600 hover:bg-green-400 text-black font-black py-5 uppercase italic text-lg shadow-[0_0_30px_rgba(34,197,94,0.2)] active:scale-95 transition-all">
                Authorize Establishment
              </button>
            </form>
          </div>
        )}

        {viewMode === "SEARCH" && (
          <div className="animate-in slide-in-from-bottom-4 duration-500">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-2xl font-black text-white italic uppercase">
                  Signal Detected: Active Crews
                </h3>
                <p className="text-[10px] text-zinc-500 uppercase">
                  Intelligence gathered from local networks
                </p>
              </div>
              <button
                onClick={() => setViewMode("LANDING")}
                className="text-zinc-600 hover:text-white transition-colors"
              >
                <XCircle size={24} />
              </button>
            </div>

            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-4 scrollbar-hide">
              {allianceList.map((a) => (
                <div
                  key={a.id}
                  className="bg-zinc-900 border border-zinc-800 p-5 flex justify-between items-center group hover:border-blue-600 transition-all shadow-inner"
                >
                  <div className="flex items-center gap-5">
                    <div className="w-12 h-12 bg-black border-2 border-blue-900 flex items-center justify-center text-blue-500 font-black italic shadow-[0_0_10px_rgba(30,58,138,0.3)]">
                      [{a.tag}]
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-white uppercase">
                        {a.name}
                      </h4>
                      <p className="text-[9px] text-zinc-500 uppercase font-bold tracking-widest">
                        Leader: {a.leader_name} | Tech Level {a.level}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-10">
                    <div className="text-right">
                      <p className="text-[9px] text-zinc-600 uppercase font-black">
                        Slots
                      </p>
                      <p
                        className={`text-sm font-black ${
                          a.member_count >= a.max_slots
                            ? "text-red-500"
                            : "text-zinc-400"
                        }`}
                      >
                        {a.member_count} / {a.max_slots}
                      </p>
                    </div>
                    {a.hasRequested ? (
                      <span className="text-xs font-black uppercase text-yellow-500 flex items-center gap-2">
                        <Clock size={14} /> Pending
                      </span>
                    ) : (
                      <button
                        onClick={() => handleJoinAlliance(a.id)}
                        disabled={a.member_count >= a.max_slots}
                        className="bg-blue-600 hover:bg-blue-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-black px-6 py-3 uppercase italic text-xs flex items-center gap-2 transition-all active:scale-95"
                      >
                        <UserPlus size={14} />{" "}
                        {a.member_count >= a.max_slots ? "Full" : "Apply"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {allianceList.length === 0 && (
                <div className="text-center py-20 bg-zinc-900/30 border border-dashed border-zinc-800">
                  <p className="text-zinc-600 text-xs italic uppercase">
                    The block is quiet. Establish the first crew.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- RENDERING: ACTIVE ALLIANCE HQ ---
  const isLeader = stats.id === alliance.leader_id;
  const tabs = [
    "OVERVIEW",
    "MEMBERS",
    "FEED",
    "TREASURY",
    "UPGRADES",
    "MISSIONS",
  ];
  if (isLeader) {
    tabs.push("INVITE");
    tabs.push("RECRUITMENT");
    tabs.push("MANAGEMENT");
  }

  return (
    <div className="bg-zinc-950 border-2 border-blue-900 shadow-[0_0_60px_rgba(30,58,138,0.2)] font-mono">
      {/* HQ HEADER */}
      <div className="p-8 border-b border-blue-900 bg-blue-900/10 flex justify-between items-center relative overflow-hidden">
        <Shield
          size={180}
          className="absolute -left-10 -bottom-10 text-blue-900/10 rotate-12 pointer-events-none"
        />
        <div className="flex items-center gap-6 relative z-10">
          <div className="p-4 bg-black border border-blue-800 shadow-[0_0_25px_rgba(30,58,138,0.4)]">
            <Shield className="text-blue-500" size={40} />
          </div>
          <div>
            <h2 className="text-4xl font-black text-white uppercase italic leading-none tracking-tighter">
              {alliance.name}{" "}
              <span className="text-blue-600">[{alliance.tag}]</span>
            </h2>
            <p className="text-[10px] text-zinc-500 uppercase tracking-[0.3em] mt-2 font-bold">
              Tactical Operations Center | Generation {alliance.level}
            </p>
          </div>
        </div>
        <div className="text-right relative z-10">
          <p className="text-[10px] text-zinc-600 uppercase font-black">
            Net Defense Power
          </p>
          <p className="text-4xl font-black text-blue-500 flex items-center justify-end gap-2 italic">
            <Zap size={24} className="fill-blue-500" />{" "}
            {alliance.total_power?.toLocaleString()}
          </p>
        </div>
      </div>

      {/* HQ NAVIGATION */}
      <div className="flex border-b border-zinc-800 bg-black/80 sticky top-0 z-20 overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 min-w-[100px] py-4 text-[10px] font-black uppercase italic transition-all border-b-2 ${
              activeTab === tab
                ? "bg-blue-600/10 text-white border-blue-500"
                : "text-zinc-600 border-transparent hover:text-zinc-300 hover:bg-zinc-900/50"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* TAB CONTENT */}
      <div className="p-10 min-h-[500px] bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-blue-900/5 via-transparent to-transparent">
        {activeTab === "OVERVIEW" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="md:col-span-2 space-y-6">
              <div className="bg-zinc-900 p-6 border-l-4 border-blue-600 shadow-xl">
                <h4 className="text-white text-xs font-black uppercase mb-3 flex items-center gap-2">
                  <Crown size={16} className="text-yellow-500" /> Crew
                  Leadership
                </h4>
                <p className="text-zinc-500 text-[11px] uppercase font-bold tracking-widest">
                  Authorized Commander:{" "}
                  <span className="text-white ml-2 text-sm">
                    {alliance.leader_name}
                  </span>
                </p>
              </div>

              <div className="bg-zinc-900 p-6 border-l-4 border-zinc-700 shadow-xl">
                <h4 className="text-white text-xs font-black uppercase mb-3 flex items-center gap-2">
                  <Target size={16} className="text-red-500" /> Strategic
                  Intelligence
                </h4>
                <p className="text-zinc-500 text-[11px] uppercase font-bold tracking-widest">
                  Network Capacity:{" "}
                  <span className="text-white ml-2 text-sm">
                    {alliance.members?.length} / {alliance.max_slots} Members
                  </span>
                </p>
              </div>

              <div className="p-6 bg-blue-950/20 border border-blue-900/30">
                <h4 className="text-blue-400 text-[10px] font-black uppercase mb-2">
                  Tactical Memo
                </h4>
                <p className="text-zinc-500 text-xs italic leading-relaxed">
                  "Collective security protocols are active. Attacks on
                  individual members will trigger alliance-wide inventory
                  deployment."
                </p>
              </div>
            </div>

            <div className="border border-zinc-800 p-8 flex flex-col justify-center items-center text-center bg-black/60 shadow-2xl relative overflow-hidden">
              <Landmark
                size={80}
                className="text-zinc-900 absolute -top-4 -right-4 rotate-12"
              />
              <div className="relative z-10">
                <Landmark size={56} className="text-blue-900 mb-4 mx-auto" />
                <h4 className="text-zinc-500 text-[10px] uppercase font-black tracking-[0.3em]">
                  War Chest
                </h4>
                <p className="text-4xl font-black text-white mt-2 italic">
                  ${alliance.funds?.toLocaleString()}
                </p>
                <p className="text-[9px] text-zinc-700 uppercase mt-4 font-bold tracking-tighter italic">
                  Liquid Assets for HQ Expansion
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "MEMBERS" && (
          <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 gap-3">
              {alliance.members?.map((member) => (
                <div
                  key={member.id}
                  className="bg-zinc-900 border border-zinc-800 p-4 flex justify-between items-center group hover:border-blue-500 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-black flex items-center justify-center text-xs font-black text-blue-500 border border-zinc-800 shadow-inner">
                      {member.level}
                    </div>
                    <div>
                      <span className="text-white font-black text-sm uppercase italic flex items-center gap-2">
                        {member.username}
                        {member.id === alliance.leader_id && (
                          <Crown
                            size={14}
                            className="text-yellow-500 fill-yellow-500"
                          />
                        )}
                      </span>
                      <p className="text-[9px] text-zinc-600 uppercase font-bold">
                        Active Tactical Unit
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-8">
                    <div className="text-right">
                      <span className="text-[10px] text-zinc-600 uppercase font-black">
                        Power Rating{" "}
                      </span>
                      <span className="text-lg font-black text-blue-500 italic block">
                        {member.street_power?.toLocaleString()}
                      </span>
                    </div>
                    {isLeader && member.id !== stats.id && (
                      <button
                        onClick={() => handleKick(member.id)}
                        className="text-zinc-800 hover:text-red-600 transition-all p-2 bg-black/40 border border-transparent hover:border-red-900"
                        title="PURGE MEMBER"
                      >
                        <XCircle size={20} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-zinc-900 pt-8">
              <button
                onClick={handleLeave}
                className="w-full bg-red-900/10 hover:bg-red-700 text-red-700 hover:text-white border border-red-900 font-black py-4 uppercase italic text-xs transition-all tracking-widest active:scale-[0.99]"
              >
                {isLeader
                  ? "Decommission Tactical Network (Disband)"
                  : "Terminate Connection (Leave Crew)"}
              </button>
            </div>
          </div>
        )}

        {activeTab === "RECRUITMENT" && isLeader && (
          <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-black text-white italic uppercase">
                Pending Applications
              </h3>
              <p className="text-[10px] text-zinc-500 uppercase font-bold">
                Reviewing {requests.length} Candidates
              </p>
            </div>

            {requests.length === 0 ? (
              <div className="text-center py-20 bg-zinc-900/30 border border-dashed border-zinc-800">
                <Users size={48} className="mx-auto text-zinc-800 mb-4" />
                <p className="text-zinc-600 text-xs italic uppercase">
                  No new recruits at the gate.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {requests.map((req) => {
                  const theme = getRarityTheme(req.level);
                  return (
                    <div
                      key={req.request_id}
                      className={`relative bg-black border-2 flex flex-col justify-between group hover:scale-[1.02] transition-all ${theme.border}`}
                    >
                      <div
                        className={`p-4 border-b ${theme.border} ${theme.bg}`}
                      >
                        <div className="flex justify-between items-start">
                          <span
                            className={`text-[9px] font-black uppercase px-2 py-0.5 border ${theme.border} ${theme.textColor} bg-black`}
                          >
                            {theme.label} CANDIDATE
                          </span>
                          <span className="text-xs font-black text-white bg-black px-2 py-1 border border-zinc-800">
                            LVL {req.level}
                          </span>
                        </div>
                        <h4 className="text-lg font-black text-white uppercase italic mt-2">
                          {req.username}
                        </h4>
                      </div>

                      <div className="p-4 flex-1">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-[10px] text-zinc-500 font-bold uppercase flex items-center gap-1">
                            <Zap size={12} className="text-blue-500" /> Power
                          </span>
                          <span className="text-sm font-black text-white">
                            {req.street_power.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="p-3 bg-zinc-950 border-t border-zinc-900 grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleDeny(req.request_id)}
                          className="bg-red-900/20 hover:bg-red-600 text-red-500 hover:text-white border border-red-900 font-black py-2 uppercase italic text-[10px] transition-all"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleApprove(req.request_id)}
                          className="bg-green-600 hover:bg-green-500 text-black font-black py-2 uppercase italic text-[10px] transition-all shadow-[0_0_15px_rgba(34,197,94,0.3)]"
                        >
                          Accept
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "INVITE" && isLeader && (
          <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
            <div className="bg-zinc-900 p-8 border border-zinc-800 shadow-xl">
              <h3 className="text-2xl font-black text-white italic uppercase mb-4 flex items-center gap-2">
                <UserPlus size={24} className="text-blue-500" /> Scout Potential
                Recruits
              </h3>
              <div className="relative">
                <Search
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  type="text"
                  className="w-full bg-black border border-zinc-700 p-4 pl-12 text-white uppercase outline-none focus:border-blue-600 transition-colors"
                  placeholder="Search by Username..."
                  value={playerSearch}
                  onChange={(e) => handleSearchPlayers(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {searchResults.map((player) => (
                <div
                  key={player.id}
                  className="bg-zinc-900 border border-zinc-800 p-4 flex justify-between items-center group hover:border-blue-500 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-black flex items-center justify-center text-xs font-black text-blue-500 border border-zinc-800">
                      {player.level}
                    </div>
                    <div>
                      <h4 className="text-white font-black uppercase italic">
                        {player.username}
                      </h4>
                      <p className="text-[9px] text-zinc-600 uppercase font-bold">
                        Independent Hustler
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <span className="text-[9px] text-zinc-600 uppercase font-black">
                        Power Rating
                      </span>
                      <span className="text-sm font-black text-blue-500 block">
                        {player.street_power.toLocaleString()}
                      </span>
                    </div>
                    <button
                      onClick={() => handleInvitePlayer(player.id)}
                      className="bg-blue-600 hover:bg-blue-400 text-black font-black px-6 py-2 uppercase italic text-xs transition-all active:scale-95 shadow-lg"
                    >
                      Send Invite
                    </button>
                  </div>
                </div>
              ))}
              {playerSearch.length >= 2 && searchResults.length === 0 && (
                <div className="text-center py-10 bg-zinc-900/30 border border-dashed border-zinc-800">
                  <p className="text-zinc-600 text-xs italic uppercase">
                    No unaffiliated players found matching that signal.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "FEED" && (
          <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-500 max-h-[500px] overflow-y-auto pr-4 scrollbar-hide">
            {feed.map((entry, idx) => (
              <div
                key={idx}
                className="bg-zinc-900 border-l-2 border-blue-600 p-4 flex justify-between items-center group"
              >
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase font-black mb-1">
                    [{new Date(entry.created_at).toLocaleTimeString()}]
                  </p>
                  <p className="text-xs text-zinc-300">
                    <span className="text-white font-bold uppercase italic">
                      {entry.username}
                    </span>{" "}
                    {entry.message}
                  </p>
                </div>
                <div className="opacity-20 group-hover:opacity-100 transition-opacity">
                  {entry.event_type === "donation" && (
                    <DollarSign size={14} className="text-green-500" />
                  )}
                  {entry.event_type === "mission" && (
                    <Target size={14} className="text-red-500" />
                  )}
                  {entry.event_type === "membership" && (
                    <Users size={14} className="text-blue-500" />
                  )}
                </div>
              </div>
            ))}
            {feed.length === 0 && (
              <div className="text-center py-20 bg-zinc-900/30 border border-dashed border-zinc-800">
                <p className="text-zinc-600 text-xs italic uppercase">
                  No recent activity on this frequency.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "TREASURY" && (
          <div className="max-w-md mx-auto space-y-8 animate-in zoom-in-95 duration-500">
            <div className="text-center bg-blue-900/5 border border-blue-900/20 p-8 shadow-inner">
              <h3 className="text-2xl font-black italic text-white uppercase mb-3">
                Treasury Contribution
              </h3>
              <p className="text-[10px] text-zinc-500 uppercase leading-relaxed font-bold tracking-widest">
                Resource transfers are non-refundable. Funds are utilized by
                leadership for HQ upgrades and unit capacity expansion.
              </p>
            </div>

            <div className="relative group">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <span className="text-zinc-700 font-black text-xl">$</span>
              </div>
              <input
                type="number"
                value={donationAmount}
                onChange={(e) => setDonationAmount(e.target.value)}
                className="w-full bg-black border-2 border-zinc-800 p-6 pl-10 text-center text-4xl font-black text-green-500 focus:border-blue-600 outline-none transition-colors shadow-2xl"
                placeholder="0"
              />
            </div>

            <button
              onClick={handleDonate}
              disabled={donationAmount <= 0 || stats.cash < donationAmount}
              className="w-full bg-blue-600 hover:bg-blue-400 disabled:bg-zinc-900 disabled:text-zinc-700 text-black font-black py-5 uppercase italic text-xl shadow-[0_0_30px_rgba(30,58,138,0.3)] transition-all active:scale-95"
            >
              Authorize Wire Transfer
            </button>

            <p className="text-center text-[9px] text-zinc-700 uppercase font-black italic">
              Secure Connection Verified: {window.location.hostname}
            </p>
          </div>
        )}

        {activeTab === "UPGRADES" && (
          <div className="space-y-6 animate-in fade-in duration-700">
            <div className="bg-zinc-900 border border-zinc-800 p-8 flex justify-between items-center shadow-xl group hover:border-blue-900 transition-colors">
              <div className="flex gap-6 items-center">
                <div className="p-4 bg-black border border-zinc-800 text-green-500">
                  <TrendingUp size={32} />
                </div>
                <div>
                  <h4 className="text-white font-black uppercase text-xl italic">
                    Expand Tactical Grid
                  </h4>
                  <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest mt-1">
                    Current Slots:{" "}
                    <span className="text-white">{alliance.max_slots}</span> |
                    Payload Bonus:{" "}
                    <span className="text-blue-500">+5 Units</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-zinc-600 uppercase mb-3 font-black tracking-tighter italic">
                  Upgrade Cost: $500,000
                </p>
                <button
                  disabled={!isLeader || alliance.funds < 500000}
                  onClick={handleUpgradeSlots}
                  className="bg-blue-600 disabled:bg-zinc-950 disabled:text-zinc-800 disabled:border-zinc-900 border border-transparent hover:bg-blue-400 text-black font-black px-10 py-3 uppercase italic text-sm flex items-center gap-3 transition-all active:scale-95 shadow-2xl"
                >
                  <ArrowUpCircle size={18} />{" "}
                  {isLeader ? "Authorize Upgrade" : "Access Denied"}
                </button>
              </div>
            </div>

            {!isLeader && (
              <div className="text-center py-6 border border-red-900/20 bg-red-900/5 mt-10">
                <p className="text-[10px] text-red-600 uppercase italic font-black flex items-center justify-center gap-2">
                  <Shield size={14} /> Security Alert: Authorized Commanders
                  Only
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "MANAGEMENT" && isLeader && (
          <div className="space-y-12 animate-in slide-in-from-bottom-4">
            {/* UPDATE NAME */}
            <div className="bg-zinc-900 p-8 border border-zinc-800 shadow-xl">
              <h3 className="text-xl font-black text-white uppercase italic mb-2">
                Re-Brand Organization
              </h3>
              <p className="text-[10px] text-zinc-500 uppercase mb-6 italic">
                Cost: $250,000 + 25 Turns | 7 Day Cooldown
              </p>
              <div className="flex gap-4">
                <input
                  className="flex-1 bg-black border border-zinc-700 p-3 text-white uppercase"
                  placeholder="ENTER NEW IDENTITY"
                  value={updateName}
                  onChange={(e) => setUpdateName(e.target.value)}
                />
                <button
                  onClick={handleUpdateName}
                  className="bg-blue-600 px-8 py-3 text-black font-black uppercase italic"
                >
                  Apply Change
                </button>
              </div>
            </div>

            {/* UPDATE TAG */}
            <div className="bg-zinc-900 p-8 border border-zinc-800 shadow-xl">
              <h3 className="text-xl font-black text-white uppercase italic mb-2">
                Update Tactical Tag
              </h3>
              <p className="text-[10px] text-zinc-500 uppercase mb-6 italic">
                Cost: $500,000 + 50 Turns | 7 Day Cooldown
              </p>
              <div className="flex gap-4">
                <input
                  maxLength={4}
                  className="flex-1 bg-black border border-zinc-700 p-3 text-white text-center font-black tracking-widest uppercase"
                  placeholder="TRAP"
                  value={updateTag}
                  onChange={(e) => setUpdateTag(e.target.value.toUpperCase())}
                />
                <button
                  onClick={handleUpdateTag}
                  className="bg-green-600 px-8 py-3 text-black font-black uppercase italic"
                >
                  Update Tag
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "MISSIONS" && (
          <div className="space-y-8 animate-in fade-in duration-500">
            {activeMission ? (
              <div className="bg-zinc-900 border-2 border-red-900 p-8 relative overflow-hidden">
                <h3 className="text-3xl font-black text-white italic uppercase mb-2">
                  {activeMission.title}
                </h3>
                <p className="text-zinc-500 text-xs mb-6 italic">
                  "{activeMission.description}"
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between text-[10px] font-black uppercase">
                    <span className="text-zinc-400">Current Progress</span>
                    <span className="text-red-500">
                      {activeMission.current_progress.toLocaleString()} /{" "}
                      {activeMission.objective_target.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full h-4 bg-black border border-zinc-800">
                    <div
                      className="h-full bg-red-600 shadow-[0_0_15px_rgba(220,38,38,0.4)] transition-all duration-1000"
                      style={{
                        width: `${Math.min(
                          100,
                          (activeMission.current_progress /
                            activeMission.objective_target) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                  {activeMission.current_progress >=
                    activeMission.objective_target && (
                    <button
                      onClick={handleClaimMission}
                      className="w-full mt-6 bg-green-600 hover:bg-green-400 text-black font-black py-4 uppercase italic text-sm shadow-[0_0_20px_rgba(34,197,94,0.3)] animate-bounce"
                    >
                      Claim Mission Reward ($
                      {activeMission.reward_cash?.toLocaleString()})
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center py-10 bg-blue-900/5 border border-dashed border-blue-900/30">
                  <Target size={48} className="mx-auto text-blue-900 mb-4" />
                  <h3 className="text-xl font-black text-white uppercase italic">
                    Mission Briefing
                  </h3>
                  <p className="text-xs text-zinc-500 uppercase">
                    No active objectives. Select a contract to mobilize the
                    crew.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {availableMissions.map((m) => (
                    <div
                      key={m.id}
                      className="bg-zinc-900 border border-zinc-800 p-6 flex justify-between items-center group hover:border-blue-600 transition-all"
                    >
                      <div>
                        <h4 className="text-lg font-black text-white uppercase italic">
                          {m.title}
                        </h4>
                        <p className="text-[10px] text-zinc-500 uppercase mb-2">
                          {m.description}
                        </p>
                        <div className="flex gap-4 text-[9px] font-bold uppercase">
                          <span className="text-blue-500">
                            Target: {m.objective_target.toLocaleString()}{" "}
                            {m.objective_type.replace("_", " ")}
                          </span>
                          <span className="text-green-500">
                            Reward: ${m.reward_cash.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      {isLeader ? (
                        <button
                          onClick={() => handleStartMission(m.id)}
                          className="bg-blue-600 hover:bg-blue-400 text-black font-black px-6 py-3 uppercase italic text-xs transition-all"
                        >
                          Start Mission
                        </button>
                      ) : (
                        <span className="text-[10px] text-zinc-700 italic uppercase">
                          Waiting for Leader
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
